import { getStore } from '../store/store';
import type BrowserWidthBucket from '../store/schema/BrowserWidthBucket';
import BrowserHeightBucket from '../store/schema/BrowserHeightBucket';
import LayoutChangeSource from '../store/schema/LayoutChangeSource';
import calculateAvailableWidthBucket from './calculateAvailableWidthBucket';
import calculateAvailableHeightBucket from './calculateAvailableHeightBucket';
import { onAvailableWidthBucketChanged } from '../actions/onAvailableWidthBucketChanged';
import setAreDisplayAdsEnabled from '../mutators/setAreDisplayAdsEnabled';
import { getAreDisplayAdsEnabled } from '../selectors/getAreDisplayAdsEnabled';
import isFlexPaneShownHelper from 'owa-app-pane-group-utils/lib/helpers/isFlexPaneShownHelper';
import {
    setAvailableHeightAndWidthBuckets,
    setBrowserWidthBucket,
    setBrowserHeightBucket,
    setMainAppHeightOverflowMutator,
} from '../actions/setAvailableHeightAndWidthBuckets';
import { updateFlexPaneInternalWidth } from './updateFlexPaneInternalWidth';
import { addEventTimeToSourceMapping } from 'owa-performance';
import { getPendingAutoOpenFlexPaneId } from 'owa-suite-header-store';
import { isCapabilityEnabled } from 'owa-capabilities';
import { newHeaderCapability } from 'owa-capabilities-definitions/lib/newHeaderCapability';

const MAIN_APP_MIN_HEIGHT = '600px';

// This is needed to prevent the event listener function to change on each module switch
// which prevents it from being removed/updated successfully
let OVERFLOW_WIDTH_BUCKET = 418;
let dynamicLayoutInitialized = false;
const OVERFLOW_HEIGHT_BUCKET = BrowserHeightBucket.From580_To600;
let mainAppMinHeight = MAIN_APP_MIN_HEIGHT;

export function initializeDynamicLayout(
    areDisplayAdsEnabled: boolean,
    customOverflowWidthBucket?: BrowserWidthBucket,
    customMainAppMinHeight?: string
): void {
    // Update OVERFLOW_WIDTH_BUCKET to save the preferred overflowWidthBucket so the event listener
    // is not affected by the variable value of the overflowWidthBucket
    OVERFLOW_WIDTH_BUCKET = customOverflowWidthBucket ?? 418;

    mainAppMinHeight = customMainAppMinHeight ?? MAIN_APP_MIN_HEIGHT;

    // Set dynamicLayoutInitialized as true so we only show the vertical scrollbars when the flow has initialized dynamicLayout
    dynamicLayoutInitialized = true;

    // Reserve space for any pending auto open operation
    const flexPaneId = !isCapabilityEnabled(newHeaderCapability)
        ? getPendingAutoOpenFlexPaneId()
        : undefined;

    // Initialize custom property for flexpane width
    updateFlexPaneInternalWidth(flexPaneId);

    // Add event listener with new overflow width bucket
    // No need to call removeEventListener while the name of the event remains the same
    window.addEventListener('resize', onWindowResize);

    setAreDisplayAdsEnabled(areDisplayAdsEnabled);

    // Calculate once on boot.
    setAvailableHeightAndWidthBuckets(LayoutChangeSource.Init);
}

/**
 * On Window resize handler.
 */
function onWindowResize(event: UIEvent) {
    addEventTimeToSourceMapping(event, 'initializeDynamicLayout');
    setAvailableHeightAndWidthBuckets(LayoutChangeSource.WindowResize);
}

/**
 * Update the layout on the available app width change
 * @param source source of the width change
 */
export function setAvailableWidthBucket(source: LayoutChangeSource): void {
    // Update available app width bucket
    const availableWidthBucket = calculateAvailableWidthBucket(
        source != LayoutChangeSource.Init,
        getAreDisplayAdsEnabled(),
        isFlexPaneShownHelper()
    );

    // No-op if the width bucket did not change
    if (getStore().browserWidthBucket == availableWidthBucket) {
        return;
    }

    setBrowserWidthBucket(availableWidthBucket);

    if (availableWidthBucket <= OVERFLOW_WIDTH_BUCKET) {
        addOverflowXOnBody();
    } else {
        hideOverflowXOnBody();
    }

    /* action to listen to for consumers to know that the availableWidthBucket had updated,
     * passes new availableWidthBucket
     */
    onAvailableWidthBucketChanged(availableWidthBucket, source);
}

/**
 * Update the layout on the available app height change
 * @param source source of the height change
 */
export function setAvailableHeightBucket(source: LayoutChangeSource, forceUpdate?: boolean): void {
    // Update available app height bucket
    const availableHeightBucket = calculateAvailableHeightBucket(source != LayoutChangeSource.Init);

    // No-op if the height bucket did not change
    if (!forceUpdate && getStore().browserHeightBucket == availableHeightBucket) {
        return;
    }

    setBrowserHeightBucket(availableHeightBucket);

    if (dynamicLayoutInitialized) {
        setMainAppHeightOverflowMutator(
            mainAppMinHeight,
            availableHeightBucket <= OVERFLOW_HEIGHT_BUCKET
        );
    }
}

export function addOverflowXOnBody(): void {
    if (window.document?.body) {
        document.body.style.overflowX = 'auto';
    }
}

export function hideOverflowXOnBody(): void {
    if (window.document?.body) {
        document.body.style.overflowX = 'hidden';
    }
}

export function getMainAppScrollableParentElement(): HTMLElement | null {
    const mainAppElement = document.getElementById('mainApp');
    return mainAppElement?.parentElement ?? null;
}
